import React, { useCallback } from 'react';
import {
  SlDialog,
  SlDialogRoot,
  SlDialogTitle,
  SlDialogCloseButton,
  SlDialogContent
} from 'src/components/BeaconV2Components/SlDialog';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { topicSettingsServerProxy } from '../serverProxy';
import { useMutation } from '@tanstack/react-query';
import { GenericErrorMessage } from 'src/components/BeaconV2Components/common/CommonSnackbarMessages/GenericErrorMessage';
import { useSnackbar } from 'src/utils/Hooks';
import { useRemoveTopicModal } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/hooks/useRemoveTopicModal';
import { useTopicPolling } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/hooks/useTopicPolling';
import { useTopicTaxonomyOptions } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/hooks/useTopicTaxonomyOptions';
import { useRefreshTopicEntity } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/hooks/useRefreshTopicEntity';
import { useMainEntity } from 'src/utils/Hooks/reduxSelectors';

const getModalText = (variant: 'topic' | 'topicGroup', name?: string) => {
  switch (variant) {
    case 'topic':
      return {
        header: 'Remove Topic from Group?',
        body: `Are you sure you want to remove the ${name} topic from this topic group?`,
        message: 'You’ve successfully removed a topic from this topic group.'
      };
    case 'topicGroup':
      return {
        header: 'Remove from Topic Group?',
        body: `Are you sure you want to remove this topic from the ${name} topic group?`,
        message: 'You’ve successfully removed this topic from a topic group.'
      };

    default:
      return {
        header: 'Remove Topic from Group?',
        body: `Are you sure you want to remove the ${name} topic from this topic group?`,
        message: 'You’ve successfully removed a topic from this topic group.'
      };
  }
};

/**
 * Modal used for removing topics from an owned topic group.
 * Controlled via the useDeleteTopicModal hook
 */
export const RemoveTopicModal = () => {
  const mainEntity = useMainEntity();
  const { isOpen, closeModal, topicId, topicGroupId, topicName, topicGroupName } = useRemoveTopicModal();
  const { showSnackbar } = useSnackbar();
  const { pollTopicGroupUpdate } = useTopicPolling();
  const refreshEntity = useRefreshTopicEntity(mainEntity);

  const taxonomyOptions = useTopicTaxonomyOptions();

  const text = getModalText(topicGroupName ? 'topicGroup' : 'topic', topicGroupName ?? topicName);

  const { mutateAsync: removeTopicsFromTopicGroup, isLoading } = useMutation({
    mutationFn: topicSettingsServerProxy.removeTopicsFromTopicGroup,
    onSuccess: () => {
      pollTopicGroupUpdate(topicGroupId, `removing-${topicGroupId}`, refreshEntity);
    }
  });

  const handleRemove = useCallback(async () => {
    if (isLoading) {
      return;
    }

    try {
      await removeTopicsFromTopicGroup({
        topicGroupId: String(topicGroupId),
        topicIds: [topicId],
        taxonomyOptions
      });

      showSnackbar({ message: text.message, type: 'success' });
    } catch (error) {
      console.error('Error removing topic from topic group: ', error);
      showSnackbar({
        type: 'error',
        message: <GenericErrorMessage />
      });
    } finally {
      closeModal();
    }
  }, [showSnackbar, removeTopicsFromTopicGroup, isLoading, topicId, topicGroupId, closeModal, text, taxonomyOptions]);

  return (
    <SlDialogRoot>
      <SlDialog open={isOpen} onClose={() => closeModal()} width="500px" sx={{ minHeight: '180px' }}>
        <SlDialogCloseButton
          onClick={() => closeModal()}
          style={{
            height: '16px',
            width: '16px'
          }}
        />
        <SlDialogContent
          sx={{
            padding: '24px 24px 22px 24px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <SlDialogTitle>{text.header}</SlDialogTitle>
          <Text sx={{ marginTop: '12px', marginBottom: '22px', overflowWrap: 'break-word' }}>{text.body}</Text>
          <Flex justifyContent="flex-end" alignItems="center" gap="md">
            <SlButton onClick={() => closeModal()}>Cancel</SlButton>
            <SlButton variant="contained" onClick={() => handleRemove()}>
              {isLoading ? 'Removing...' : 'Remove'}
            </SlButton>
          </Flex>
        </SlDialogContent>
      </SlDialog>
    </SlDialogRoot>
  );
};
